import React from 'react';
import { IAnyPlan, IRichText, ITestable, TPlanType } from '@belong/types';

import { COLOURS } from '@belong/themes';
import { MetricContainer, StackWithTopPadding } from '../../../contentBlocks/Metric/Metric.styles';
import { getAriaLabel } from '../../../contentBlocks/Metric/Metric.utils';
import { RichText } from '../../../styles/Typography/RichText/RichText';
import { Stack } from '../../../ui';
import Metric from '../../../contentBlocks/Metric';
import { isMobilePlan, isNbnPlan } from '../utils';
import { PLAN_CARD_TEST_ID } from '../testIds';

interface ISpeedBreakdown extends ITestable {
  planType: TPlanType;
  plan: IAnyPlan;
  typicalSpeedsCopy?: IRichText;
}

/**
 * Use this to consume plan details and embed the Metric and Price Breakdown into the Plan Card
 */
export const SpeedBreakdown: React.FC<ISpeedBreakdown> = ({
  planType,
  plan,
  typicalSpeedsCopy,
  'data-testid': testId
}) => {
  if (isMobilePlan(planType) && plan.data) {
    return (
      <Stack alignX="center" space="large" divider data-testid={testId}>
        {typicalSpeedsCopy && (
          <Stack alignX="center">
            <RichText {...typicalSpeedsCopy} hasColor={COLOURS.GREY_600} p="copySmall" alignment="center" />
          </Stack>
        )}
      </Stack>
    );
  }

  if (isNbnPlan(planType) && plan.networkSpeed) {
    return (
      <StackWithTopPadding alignX="center" space={{ xs: 'xsmall', md: 'small' }} data-testid={testId}>
        {typicalSpeedsCopy && (
          <RichText {...typicalSpeedsCopy} hasColor={COLOURS.GREY_600} p="copySmall" alignment="center" />
        )}
        <MetricContainer>
          {/* Metric for download */}
          <Metric
            scalingRoot="M"
            value={plan.networkSpeed.downstream.value}
            measurementUnit={plan.networkSpeed.downstream.unit}
            description="download"
            ariaLabel={`${getAriaLabel({
              value: plan.networkSpeed.downstream.value,
              measurementUnit: plan.networkSpeed.downstream.unit
            })} download speed`}
            data-testid={PLAN_CARD_TEST_ID.planDownloadMetric}
          />
          {/* Metric for upload */}
          <Metric
            scalingRoot="M"
            value={plan.networkSpeed.upstream.value}
            measurementUnit={plan.networkSpeed.upstream.unit}
            description="upload"
            ariaLabel={`${getAriaLabel({
              value: plan.networkSpeed.upstream.value,
              measurementUnit: plan.networkSpeed.upstream.unit
            })} upload speed`}
            data-testid={PLAN_CARD_TEST_ID.planUploadMetric}
          />
        </MetricContainer>
      </StackWithTopPadding>
    );
  }

  return null;
};

SpeedBreakdown.displayName = 'SpeedBreakdown';
